import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HomePageNew } from './pages/HomePageNew';
import { WaitTimesPageNew } from './pages/WaitTimesPageNew';
import { VisitDetailPageNew } from './pages/VisitDetailPageNew';
import { MyTripsPageNew } from './pages/MyTripsPageNew';
import { TripDetailPageNew } from './pages/TripDetailPageNew';
import { CreateVisitPageNew } from './pages/CreateVisitPageNew';
import { AllVisitsPageNew } from './pages/AllVisitsPageNew';
import { ReportsPageNew } from './pages/ReportsPageNew';
import { RatingPageNew } from './pages/RatingPageNew';
import { ReportCalendarPageNew } from './pages/ReportCalendarPageNew';
import { ReportAttractionsPageNew } from './pages/ReportAttractionsPageNew';
import { ParksPageNew } from './pages/ParksPageNew';
import { SignInPageNew } from './pages/SignInPageNew';
import { AnalyticsPageNew } from './pages/AnalyticsPageNew';
import { SharedTripPageNew } from './pages/SharedTripPageNew';
import { SharedWithMePageNew } from './pages/SharedWithMePageNew';
import { CreateTripPage } from './pages/CreateTripPage';
import { AppLayout } from './design-system/layouts/AppLayout';
import { useAuthGuard } from './hooks/useAuthGuard';
import { bootstrapSessionInterceptor } from './hooks/useSessionInterceptor';
import { useSessionWatcher } from './hooks/useSessionWatcher';
import { ErrorBoundary } from './layout/ErrorBoundary';
import { DarkModeProvider } from './providers/DarkModeProvider';
import { AuthProvider } from './contexts/AuthContext';
import { ThresholdsProvider } from './hooks/useAttractionThresholds';

bootstrapSessionInterceptor();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

function AppContent() {
  const { isAuthenticated, isLoading } = useAuthGuard();
  useSessionWatcher();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/sign-in" element={isAuthenticated ? <Navigate to="/" replace /> : <SignInPageNew />} />
      <Route path="/share/:token" element={<SharedTripPageNew />} />
      {isAuthenticated ? (
        <Route element={<AppLayout />}>
          <Route index element={<HomePageNew />} />
          <Route path="visit/:visitId" element={<VisitDetailPageNew />} />
          <Route path="create" element={<CreateVisitPageNew />} />
          <Route path="all-visits" element={<AllVisitsPageNew />} />
          <Route path="my-trips" element={<MyTripsPageNew />} />
          <Route path="shared" element={<SharedWithMePageNew />} />
          <Route path="trip/:id" element={<TripDetailPageNew />} />
          <Route path="wait-times" element={<WaitTimesPageNew />} />
          <Route path="trips/create" element={<CreateTripPage />} />
          <Route path="admin/*" element={<Outlet />}>
            <Route index element={<ReportsPageNew />} />
            <Route path="rating" element={<RatingPageNew />} />
            <Route path="reports" element={<ReportsPageNew />} />
            <Route path="reports/:tripId/calendar" element={<ReportCalendarPageNew />} />
            <Route path="reports/:tripId/attractions" element={<ReportAttractionsPageNew />} />
            <Route path="parks" element={<ParksPageNew />} />
            <Route path="analytics" element={<AnalyticsPageNew />} />
          </Route>
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      )}
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ThresholdsProvider>
            <DarkModeProvider>
            <ErrorBoundary>
              <AppContent />
            </ErrorBoundary>
            </DarkModeProvider>
          </ThresholdsProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
